import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { toastError, toastSuccess } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { updateLeadTime } from '@services/commonService';
import { onUploadInitialCostingDocuments, removeInitialCostingDocument } from '@services/costingService';

import ApiClient from '@lib/apiClient';

import { printApiError } from '@utils/Util';

const format = (number) => {
	if (!number || isNaN(number)) return null;
	return Number(number).toFixed(2);
};

const normalizeCostingReviewAssessment = (value) => {
	if (value == null || value === '') {
		return null;
	}
	if (typeof value !== 'object') {
		return value;
	}
	if (Object.keys(value).length === 0) {
		return null;
	}
	return value.constant ?? value.value ?? null;
};

// shown with 2 decimals like the other prices, e.g. 2.5 -> 2.50
const formatTargetPrice = (value) =>
	value === null || value === undefined || value === '' || Number.isNaN(parseFloat(value))
		? null
		: parseFloat(value).toFixed(2);

const generateCostingColumn = (cloneData, initialCostingId) => {
	let newArray = [];
	for (let i = 0; i < Math.max(cloneData.length, 2) + 1; i++) {
		newArray.push({
			id: cloneData[i]?.id ? cloneData[i].id : null,
			minQuantity: cloneData[i]?.minQuantity ? cloneData[i].minQuantity : null,
			price: cloneData[i]?.price ? cloneData[i].price : null,
			targetPrice: formatTargetPrice(cloneData[i]?.targetPrice ?? cloneData[i]?.target),
			initialCostingId: initialCostingId
		});
	}
	return newArray;
};

/**
 * Loads and saves the costing of a single option (OP-1, OP-2, ...).
 * It uses the same APIs as the base costing, only addressed by the
 * `initialCostingId` of the selected option instead of the product.
 */
const useCostingOptionData = (initialCostingId, productId) => {
	const dispatch = useDispatch();
	const [inputData, setInputData] = useState({});
	const [costList, setCostList] = useState([]);
	const [documents, setDocuments] = useState([]);
	const [leadTime, setLeadTime] = useState('');
	const [selectedSupplier, setSelectedSupplier] = useState({});
	const [costingHistory, setCostingHistory] = useState([]);

	const formatAllPrice = (tmpInputData) => {
		const priceFields = [
			'fabricUnitCost',
			'trimsCost',
			'embellishmentCost',
			'washCost',
			'cmCost',
			'accessoriesCost',
			'testingCost',
			'commercialCost',
			'allowance'
		];
		for (let item of priceFields) {
			tmpInputData[item] = format(tmpInputData[item]);
		}
		return tmpInputData;
	};

	const fetchInitialCosting = async () => {
		try {
			const response = await ApiClient.GET('fetchInitialCostingFromCosting', initialCostingId);
			setLeadTime(response.data?.leadTime || '');
			setDocuments(response.data?.documentResponseList || []);
			setSelectedSupplier(
				response.data?.supplier?.id
					? { id: response.data.supplier.id, value: response.data.supplier.name }
					: {}
			);
			setInputData({
				...response.data,
				...formatAllPrice(response.data),
				totalCost: format(response.data?.totalCost),
				costingReviewAssessment: normalizeCostingReviewAssessment(
					response.data?.costingReviewAssessment
				)
			});
			return response.data;
		} catch (error) {
			printApiError(error);
		}
	};

	const fetchQuantityWiseCost = async () => {
		try {
			const { data } = await ApiClient.GET('fetchQuantityWiseCostFromCosting', initialCostingId);
			let cloneData = [...(data?.quantityWiseInitialCostingResponseList || [])];
			for (let item of cloneData) {
				item.price = item.price ? Number(item.price).toFixed(2) : null;
			}
			setCostList(generateCostingColumn(cloneData, initialCostingId));
		} catch (error) {
			printApiError(error);
		}
	};

	const getCostingHistory = async () => {
		try {
			const response = await ApiClient.GET(
				'fetchCostingHistory',
				`?productId=${productId}&initialCostingId=${initialCostingId}`
			);
			const data = response?.data;
			setCostingHistory(
				Array.isArray(data) ? data : data?.costingHistoryList || data?.recostingHistoryList || []
			);
		} catch (error) {
			printApiError(error);
		}
	};

	const saveRecostingHistory = async (option) => {
		try {
			dispatch(openLoader());
			const response = await ApiClient.POST('saveRecostingHistory', {
				productId: productId,
				baseSize: inputData?.baseSize,
				moq: inputData?.moq,
				totalCost: inputData?.totalCost,
				initialCostingId: initialCostingId,
				recostingStep: option.value,
				isPoCosting: option.value === 'FINAL_PO_COSTING'
			});
			toastSuccess(response?.data?.message || 'Re-costing history saved.');
			await getCostingHistory();
		} catch (error) {
			printApiError(error);
		} finally {
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		if (!initialCostingId) {
			return;
		}
		(async () => {
			try {
				dispatch(openLoader());
				await Promise.all([fetchInitialCosting(), fetchQuantityWiseCost(), getCostingHistory()]);
			} finally {
				dispatch(closeLoader());
			}
		})();
	}, [initialCostingId]);

	const handleChange = (e) => {
		if (e.target.value && parseFloat(e.target.value) < 0) {
			e.preventDefault();
			return;
		}
		const value = e.target.value;
		setInputData((prev) => ({ ...prev, [e.target.name]: value ? value : null }));
	};

	const handleLeadTime = (e) => setLeadTime(e.target.value);

	const handleCostingChange = (e, index) => {
		if (e.target.value && parseFloat(e.target.value) < 0) {
			e.preventDefault();
			return;
		}
		setCostList((prev) =>
			prev.map((item, i) =>
				i === index ? { ...item, [e.target.name]: e.target.value ? e.target.value : null } : item
			)
		);
	};

	/**
	 * supplierId is always sent with the selected factory (null when there is none) and
	 * isFactoryCost is true whenever a factory is selected.
	 */
	const applyFactoryCostFlag = (postData = {}) => {
		const hasSupplierId = Object.prototype.hasOwnProperty.call(postData, 'supplierId');
		const supplierId =
			(hasSupplierId ? postData.supplierId : (postData?.supplier?.id ?? selectedSupplier?.id)) ?? null;
		return { ...postData, supplierId: supplierId, isFactoryCost: !!supplierId };
	};

	const postInitialCosting = async (postData, isShowToast = true) => {
		try {
			dispatch(openLoader());
			const response = await ApiClient.POST('addInitialCosting', {
				...applyFactoryCostFlag(postData),
				initialCostingId: initialCostingId
			});
			if (isShowToast) {
				toastSuccess(response.data.message);
			}
			setInputData({
				...response.data.payload,
				...formatAllPrice(response.data.payload),
				totalCost: format(response.data.payload?.totalCost),
				costingReviewAssessment: normalizeCostingReviewAssessment(
					response.data.payload?.costingReviewAssessment
				)
			});
			await fetchQuantityWiseCost();
			return response.data.payload;
		} catch (error) {
			toastError(error.response?.data?.message || 'Error saving costing');
		} finally {
			dispatch(closeLoader());
		}
	};

	/** QTY & Unit cost of the option (same payload as the base costing row) */
	const saveInitialBaseCosting = async (moq, totalCost) => {
		const postData = {
			moq: moq ? moq : null,
			totalCost: totalCost ? totalCost : null
		};
		if (inputData?.baseSize) {
			postData['baseSize'] = inputData.baseSize;
		}
		await postInitialCosting(postData);
	};

	/** Saves the whole costing of the option (size, fabric, ...) */
	const saveOptionDetails = async () => {
		const postData = {
			...inputData,
			costingReviewAssessment: normalizeCostingReviewAssessment(inputData?.costingReviewAssessment)
		};
		delete postData['basePrice'];
		await postInitialCosting(postData);
	};

	const saveBaseSize = saveOptionDetails;
	const saveFabric = saveOptionDetails;

	const saveSupplier = async (supplier) => {
		const postData = {
			...inputData,
			costingReviewAssessment: normalizeCostingReviewAssessment(inputData?.costingReviewAssessment)
		};
		delete postData['basePrice'];
		if (supplier?.id) {
			postData['supplierId'] = supplier.id;
			postData['isFactoryCost'] = true;
		} else {
			postData['supplierId'] = null;
			postData['isFactoryCost'] = false;
			delete postData['supplier'];
		}
		setSelectedSupplier(supplier?.id ? supplier : {});
		await postInitialCosting(postData, false);
	};

	/** Quantity wise cost rows of the option */
	const saveCosting = async (e, index) => {
		const { name } = e.target;
		const selectedField = costList?.find((item, i) => i === index);
		const duplicateQty = costList?.filter(
			(item) => parseFloat(item.minQuantity) === parseFloat(selectedField.minQuantity)
		);
		if (duplicateQty.length > 1) {
			toastError('This quantity already exist.');
			return;
		}
		// the first row is the base costing of the option (same as the buyer request flow)
		if (index === 0) {
			await saveInitialBaseCosting(selectedField?.minQuantity, selectedField?.price);
			return;
		}
		if (!(selectedField?.id || (selectedField?.minQuantity && selectedField?.price))) {
			return;
		}
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('saveQuantityWiseCost', {
				...selectedField,
				initialCostingId: initialCostingId
			});
			toastSuccess(data.message);
			let cloneData = [...(data.payload?.quantityWiseInitialCostingResponseList || [])];
			if (name === 'price') {
				for (let item of cloneData) {
					item.price = item.price ? Number(item.price).toFixed(2) : null;
				}
			}
			setCostList(generateCostingColumn(cloneData, initialCostingId));
		} catch (error) {
			toastError(error.response?.data?.message || 'Error saving costing');
		} finally {
			dispatch(closeLoader());
		}
	};

	/** Target price of a quantity wise cost row */
	const saveTargetPrice = async (index) => {
		const selectedField = costList?.[index];
		if (!selectedField?.id) {
			return;
		}
		try {
			dispatch(openLoader());
			const { data } = await ApiClient.POST('quantityWiseUpdatePrice', {
				id: selectedField.id,
				targetPrice: parseFloat(selectedField.targetPrice) || null
			});
			toastSuccess(data?.message || 'Target price updated successfully');
			await fetchQuantityWiseCost();
		} catch (error) {
			toastError(error.response?.data?.message || 'Error saving target price');
		} finally {
			dispatch(closeLoader());
		}
	};

	const saveLeadTime = async (e) => {
		try {
			dispatch(openLoader());
			await updateLeadTime({
				leadTime: e.target.value || null,
				initialCostingId: initialCostingId,
				productId: productId
			});
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleRemarks = (e) => {
		const value = e.target.value;
		setInputData((prev) => ({ ...prev, remarks: value }));
	};

	const saveRemarks = async () => {
		try {
			dispatch(openLoader());
			const response = await ApiClient.POST('updateCostingRemarks', {
				remarks: inputData?.remarks,
				initialCostingId: initialCostingId
			});
			toastSuccess(response.data.message);
		} catch (error) {
			toastError(error.response?.data?.message || 'Error saving remarks');
		} finally {
			dispatch(closeLoader());
		}
	};

	const onUploadDocument = async (uploadedDocuments) => {
		try {
			dispatch(openLoader());
			await onUploadInitialCostingDocuments(initialCostingId, uploadedDocuments);
			await fetchInitialCosting();
		} finally {
			dispatch(closeLoader());
		}
	};

	const removeDocumentById = async (documentId) => {
		try {
			dispatch(openLoader());
			await removeInitialCostingDocument(initialCostingId, documentId);
			await fetchInitialCosting();
		} finally {
			dispatch(closeLoader());
		}
	};

	return {
		inputData,
		costList,
		documents,
		leadTime,
		selectedSupplier,
		costingHistory,
		saveRecostingHistory,
		handleChange,
		handleCostingChange,
		handleLeadTime,
		handleRemarks,
		saveInitialBaseCosting,
		saveBaseSize,
		saveFabric,
		saveCosting,
		saveTargetPrice,
		saveLeadTime,
		saveRemarks,
		saveSupplier,
		onUploadDocument,
		removeDocumentById,
		setInputData
	};
};

export default useCostingOptionData;
